import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_voyage-squad-3/artifacts/242nersf_final%20llogo.png";

export default function Terms() {
  return (
    <div data-testid="terms-page" className="min-h-screen bg-[#FDFBF7] text-[#1A1A1A]">
      <header className="sticky top-0 z-30 bg-[#FDFBF7]/85 backdrop-blur-md border-b border-[#E5E0D8]/60">
        <div className="max-w-3xl mx-auto px-5 md:px-8 h-16 md:h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="terms-logo">
            <img src={LOGO_URL} alt="Fourdays" className="h-12 md:h-16 w-auto object-contain" />
          </Link>
          <Link
            to="/"
            data-testid="terms-back"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#1A1A1A] hover:bg-black text-white text-sm font-semibold px-4 py-2 transition active:scale-95"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 md:px-8 py-10 md:py-16">
        <h1 className="font-display font-extrabold text-3xl md:text-5xl tracking-tight">
          Terms of Service
        </h1>
        <p className="text-sm text-[#5C5C5C] mt-2">Last updated: 16 June 2026</p>

        <div className="mt-8 text-[15px] md:text-base leading-relaxed text-[#1A1A1A] space-y-5">
          <p>
            Welcome to <strong>Fourdays</strong> ("we", "us", "our"). These Terms of Service
            ("Terms") govern your use of our website, sign-up forms, and WhatsApp-based matching
            service (the "Service"). By using the Service, you agree to these Terms. If you do not
            agree, please do not use the Service.
          </p>
          <p>Fourdays is currently a limited beta operating in Goa, India.</p>

          <Section title="1. What Fourdays is">
            <p>
              Fourdays helps travellers find and form small groups (typically 4–5 people) for shared
              activities and routes within a local area. During the beta, matches are coordinated
              manually and groups are organised over WhatsApp.
            </p>
            <p>
              Fourdays is an introduction and coordination service only. We help travellers find
              each other. We do not organise, supervise, lead, or take responsibility for the
              activities, trips, meetups, or interactions that take place between users.
            </p>
          </Section>

          <Section title="2. Eligibility">
            <p>
              You must be at least <strong>18 years old</strong> to use the Service. By using it,
              you confirm that you are 18 or older and that the information you provide is accurate.
            </p>
          </Section>

          <Section title="3. Your responsibilities">
            <p>You agree to:</p>
            <ul className="list-disc pl-5 space-y-1.5 marker:text-[#FF5A36]">
              <li>Provide accurate information about yourself.</li>
              <li>Treat other travellers with respect.</li>
              <li>Make your own decisions about whether to meet, travel, or do activities with others.</li>
              <li>
                Take responsibility for your own safety, belongings, transport, costs, and wellbeing
                during any meetup or activity.
              </li>
              <li>Comply with all applicable laws.</li>
            </ul>
            <p>
              You agree not to use the Service to harass, harm, deceive, solicit, or endanger
              others, or for any unlawful purpose.
            </p>
          </Section>

          <Section title="4. Safety — important">
            <p>
              Your safety is something we care about, and we apply measures such as small-group
              limits, optional women-only groups, and active involvement during the beta. However,
              these measures are not guarantees.
            </p>
            <p>You understand and agree that:</p>
            <ul className="list-disc pl-5 space-y-1.5 marker:text-[#FF5A36]">
              <li>
                We do not conduct background checks or identity verification on users, and we cannot
                confirm that any user is who they claim to be.
              </li>
              <li>Other users are strangers, and meeting strangers carries inherent risks.</li>
              <li>
                You are solely responsible for assessing the people you meet and the situations you
                enter, and for your own safety at all times.
              </li>
              <li>We are not responsible for the conduct of any user, whether online or in person.</li>
            </ul>
            <p>
              If you ever feel unsafe, remove yourself from the situation and contact local
              emergency services.
            </p>
          </Section>

          <Section title="5. Assumption of risk">
            <p>
              You participate in any meetup, activity, route, trip, or interaction arranged through
              the Service entirely <strong>at your own risk</strong>. Travel and in-person activities
              involve risks including (without limitation) accident, injury, illness, loss, theft,
              and the conduct of other people. By using the Service, you knowingly and voluntarily
              accept these risks.
            </p>
          </Section>

          <Section title="6. Limitation of liability">
            <p>To the maximum extent permitted by law:</p>
            <ul className="list-disc pl-5 space-y-1.5 marker:text-[#FF5A36]">
              <li>The Service is provided "as is" and "as available", without warranties of any kind.</li>
              <li>
                We are not liable for any injury, loss, damage, theft, dispute, cost, or harm
                arising from your use of the Service, from any meetup or activity, or from the
                conduct of any user.
              </li>
              <li>
                We are not responsible for the accuracy of information provided by users, for
                whether any match or meetup happens, or for the experience of any meetup.
              </li>
            </ul>
            <p>Nothing in these Terms excludes liability that cannot be excluded under applicable law.</p>
          </Section>

          <Section title="7. WhatsApp and third parties">
            <p>
              We use WhatsApp and other third-party tools to operate the Service. Your use of those
              tools is also subject to their own terms and policies. We are not responsible for
              third-party services.
            </p>
          </Section>

          <Section title="8. Your data">
            <p>
              Our handling of your personal information is described in our{" "}
              <Link to="/privacy" className="text-[#FF5A36] font-semibold hover:underline">
                Privacy Policy
              </Link>
              , which forms part of these Terms. By using the Service, you also agree to the Privacy
              Policy.
            </p>
          </Section>

          <Section title="9. Suspension and removal">
            <p>
              We may decline to match, remove from groups, or stop providing the Service to anyone,
              at any time, at our discretion — including where we have safety or conduct concerns.
              You may stop using the Service and ask us to delete your data at any time.
            </p>
          </Section>

          <Section title="10. Beta status">
            <p>
              The Service is an early-stage beta. It may be changed, paused, or discontinued at any
              time, and may contain errors or interruptions. Features and processes described in our
              marketing may evolve.
            </p>
          </Section>

          <Section title="11. Changes to these Terms">
            <p>
              We may update these Terms as the Service develops. We will post the updated version
              with a new "Last updated" date. Continued use of the Service after changes means you
              accept the updated Terms.
            </p>
          </Section>

          <Section title="12. Governing law">
            <p>
              These Terms are governed by the laws of India. Any disputes will be subject to the
              courts of Delhi, India.
            </p>
          </Section>

          <p className="text-sm text-[#5C5C5C] italic pt-6 border-t border-[#E5E0D8]">
            This document is a general template provided for an early-stage beta and does not
            constitute legal advice. Given that the Service involves matching strangers for in-person
            meetings, you should have these Terms — especially the safety, assumption-of-risk, and
            liability sections — reviewed by a qualified lawyer before scaling beyond the beta.
          </p>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="pt-2">
      <h2 className="font-display font-extrabold text-xl md:text-2xl tracking-tight mt-6 mb-3">
        {title}
      </h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}
